import axios from 'axios'

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL
})

// Request interceptor to add the auth token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}, error => {
  return Promise.reject(error)
})

// Helper function to transform post data (adjust as needed for new structure)
const transformPost = (post) => {
  if (!post) return null;
  // Use _id consistently
  const transformed = { ...post, id: post._id }; 
  delete transformed._id;
  
  // Transform author if present
  if (post.author && post.author._id) {
    transformed.author = {
      ...post.author,
      id: post.author._id
    };
    delete transformed.author._id;
  }
  
  // Excerpt might need to come from draft content if fetching published list
  // This helper might need context or be used differently now
  // if (post.content) {
  //   transformed.excerpt = post.content.slice(0, 150) + '...';
  // }
  
  // Initialize likes/viewCount if not present
  transformed.likes = transformed.likes || 0;
  transformed.viewCount = transformed.viewCount || 0;

  // Dates are likely already Date objects from Mongoose, formatting done in component
  // if (transformed.lastSavedAt) { ... } 

  return transformed;
}

// --- Auth ---
export const signup = async (userData) => {
  const response = await api.post('/signup', userData)
  return response.data
}

export const login = async (credentials) => {
  const response = await api.post('/login', credentials)
  return response.data
}

export const getUserProfile = async () => {
  const response = await api.get('/dashboard/profile')
  return response.data
}

// --- Posts (Public/Published) ---
// Update getPosts to accept and pass the tag parameter
export const getPosts = async (page = 1, perPage = 20, includeFeatured = false, tag = null) => { 
  const defaultResponse = { posts: [], topPosts: [], pagination: { total: 0, page, perPage, totalPages: 0 } };
  try {
    // Add tag to the log
    const response = await api.get('/posts', { // Endpoint fetches published posts
      params: {
        page,
        per_page: perPage,
        include_featured: includeFeatured,
        tag: tag // Add tag to request parameters
      },
      validateStatus: (status) => status === 200,
    })
    
    if (!response.data || typeof response.data === 'string') {
      console.error('Invalid response data:', response.data)
      return { posts: [], topPosts: [], pagination: { total: 0, page, perPage, totalPages: 0 } }
    }
    
    // Check if response is the new format with topPosts and pagination
    if (response.data.posts && Array.isArray(response.data.posts)) {
      return {
        posts: response.data.posts.map(transformPost).filter(Boolean),
        topPosts: response.data.topPosts.map(transformPost).filter(Boolean),
        pagination: response.data.pagination
      }
    } else if (Array.isArray(response.data)) {
      // Handle old format for backward compatibility
      console.warn('API returned old format response')
      return {
        posts: response.data.map(transformPost).filter(Boolean),
        topPosts: [],
        pagination: { 
          total: parseInt(response.headers['x-total-count']) || 0,
          page,
          perPage,
          totalPages: Math.ceil((parseInt(response.headers['x-total-count']) || 0) / perPage)
        }
      }
    }
    
    return defaultResponse; // Use defaultResponse here
  } catch (error) {
    console.error('API: Error fetching posts:', error.response?.data || error.message || error);
    return defaultResponse; // Return default structure on error
  }
}

export const getPost = async (id) => {
  // Fetches single *published* post with content
  try {
    const response = await api.get(`/posts/${id}`);
    // Response includes content fetched from published draft on backend
    return transformPost(response.data); 
  } catch (error) {
    console.error('Error fetching post:', error);
    throw error;
  }
};

export const searchPosts = async (query) => {
  // Searches published posts
  try {
    const response = await api.get('/posts/search', { params: { q: query } })
    // Assuming backend returns content or excerpt for search results
    return Array.isArray(response.data) ? response.data.map(transformPost) : []
  } catch (error) {
    console.error('Error searching posts:', error)
    return []
  }
}

export const getRelatedPosts = async (postId) => {
  // Fetches related *published* posts
  try {
    const response = await api.get(`/posts/${postId}/related`)
    return Array.isArray(response.data) ? response.data.map(transformPost) : []
  } catch(error) {
     console.error('Error fetching related posts:', error)
     return []
  }
}

// --- Drafts & Editing ---

// Creates an initial Draft for a new post concept
export const createPost = async (initialDraftData) => {
  try {
    // Calls POST /api/drafts, returns { postId, draftId, draft }
    const response = await api.post('/drafts', initialDraftData);
    return response.data; 
  } catch (error) {
    console.error('Error in createPost (create initial draft) API call:', error.response?.data || error);
    throw error;
  }
};

// Fetches all drafts (current and versions) for a post
export const getDrafts = async (postId) => {
  try {
    const response = await api.get(`/posts/${postId}/drafts`);
    // Return raw draft data, transformation might happen in store/component
    return response.data; 
  } catch (error) {
    console.error(`Error fetching drafts for post ${postId}:`, error);
    throw error; // Let store action handle error state
  }
};

// Updates the current draft (Auto-Save)
export const updateCurrentDraft = async (postId, draftData) => {
  try {
    // Corrected URL: Use /drafts/ instead of /posts/
    const response = await api.put(`/drafts/${postId}/current`, draftData); 
    return response.data; // Return updated draft
  } catch (error) {
    console.error(`Error updating current draft for post ${postId}:`, error);
    throw error;
  }
};

// Saves the current state as a new draft version (functionally same as auto-save)
export const saveDraftVersion = async (postId, draftData) => { // Now accepts draftData
  try {
    // Call the same endpoint as updateCurrentDraft (auto-save)
    const response = await api.put(`/drafts/${postId}/current`, draftData); 
    // Returns the newly saved draft object
    return response.data; 
  } catch (error) {
    console.error(`Error saving draft version for post ${postId}:`, error);
    throw error;
  }
};

// Publishes a specific draft
export const publishPost = async (postId, draftId) => {
  try {
    // POST request with draftId in body
    const response = await api.post(`/posts/${postId}/publish`, { draftId }); 
    // Returns the updated (published) Post object
    return transformPost(response.data); 
  } catch (error) {
    console.error('Error publishing post:', error);
    throw error;
  }
}

// Delete a specific draft version
export const deleteDraftVersion = async (draftId) => {
  try {
    const response = await api.delete(`/drafts/${draftId}`);
    return response.data; // { message: 'Draft version deleted successfully.' }
  } catch (error) {
    console.error(`Error deleting draft version ${draftId}:`, error);
    throw error;
  }
};

// Deletes a post and its associated drafts (via cascade on backend)
export const deletePost = async (id) => {
  try {
    const response = await api.delete(`/posts/${id}`)
    return response.data // { message: 'Post deleted successfully' }
  } catch (error) {
    console.error('Error deleting post:', error)
    throw error
  }
}

// --- Dashboard ---
export const getUserPosts = async (type = 'all') => {
  // Fetches user's posts (published or drafts) for dashboard
  try {
    const response = await api.get('/dashboard/posts', { params: { type } });
    if (!Array.isArray(response.data)) {
      console.error('Invalid response data:', response.data);
      return [];
    }
    // Use _id from backend, transformPost adds 'id' if needed elsewhere
    return response.data; //.map(transformPost); // transformPost might not be needed if dashboard uses _id
  } catch (error) {
    console.error('Error in getUserPosts:', error);
    return [];
  }
}

// --- Tags ---
export const getTags = async () => {
  try {
    const response = await api.get('/tags'); // Gets tags with dynamic counts
    return response.data;
  } catch (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
};

export const searchTags = async (query) => {
  try {
    const response = await api.get('/tags/search', { params: { q: query } });
    // Transform the response data to the expected format { name, count }
    if (Array.isArray(response.data)) {
      return response.data.map(tag => ({
        name: tag._id || tag.name, // Assume name might be in _id or name
        count: tag.count || 0     // Assume count is in count, default to 0
      }));
    }
    return []; // Return empty array if response is not an array
  } catch (error) {
    console.error('Error searching tags:', error);
    return []; // Return empty array on error
  }
};

export const createTag = async (name) => {
  try {
    const response = await api.post('/tags', { name });
    return response.data;
  } catch (error) {
    console.error('Error creating tag:', error);
    throw error;
  }
};

// --- Likes / Bookmarks / Comments / Views ---
// Assuming these functions exist and work correctly, just removing unused params from placeholders
export const likePost = async (postId) => { // postId is used in the actual call
    // Actual implementation needed here, e.g.:
     const response = await api.post(`/posts/${postId}/like`);
    // return transformPost(response.data);
    // console.warn("likePost API function needs implementation"); 
     return transformPost(response.data);
};
export const unlikePost = async (postId) => { // postId is used in the actual call
    // Actual implementation needed here, e.g.:
     const response = await api.delete(`/posts/${postId}/like`);
    // return transformPost(response.data);
    // console.warn("unlikePost API function needs implementation"); 
     return transformPost(response.data);
};
export const bookmarkPost = async (postId) => { // postId is used in the actual call
    // Actual implementation needed here, e.g.:
     const response = await api.post(`/posts/${postId}/bookmark`);
    // return response.data; // Might not return full post
    // console.warn("bookmarkPost API function needs implementation"); 
     return response.data; // Might not return full post
};
export const unbookmarkPost = async (postId) => { // postId is used in the actual call
    // Actual implementation needed here, e.g.:
     const response = await api.delete(`/posts/${postId}/bookmark`);
    // return response.data;
    // console.warn("unbookmarkPost API function needs implementation"); 
     return response.data;
};
export const getUserLikes = async () => { // No parameters needed
    // Actual implementation needed here, e.g.:
     const response = await api.get('/user/likes');
    // return response.data.map(id => id.toString());
    // console.warn("getUserLikes API function needs implementation"); 
     return response.data.map(id => id.toString());
    // return []; // Return empty array as placeholder
};
export const getUserBookmarks = async () => { // No parameters needed
    // Actual implementation needed here, e.g.:
     const response = await api.get('/user/bookmarks');
    // return response.data.map(id => id.toString());
    // console.warn("getUserBookmarks API function needs implementation"); 
     return response.data.map(id => id.toString());
    // return []; // Return empty array as placeholder
};
export const addComment = async (postId, content) => { // Both params are used
    // Actual implementation needed here, e.g.:
     const response = await api.post(`/posts/${postId}/comments`, { content });
    // return response.data;
    // console.warn("addComment API function needs implementation"); 
     return response.data;
};

// Increments the view count for a post
export const incrementViewCount = async (postId) => {
  try {
    const response = await api.post(`/posts/${postId}/view`); 
    return response; // Return full response for status check
  } catch (error) {
    console.error(`Error incrementing view count for post ${postId}:`, error);
    // Don't throw, let store action handle logging
  }
};

// --- Obsolete ---
// export const updatePost = async (id, postData) => { ... } // Replaced by updateCurrentDraft
// export const archivePost = async (id, shouldArchive) => { ... } // Needs review/reimplementation

export default api
